import _ from 'lodash';
import type { Client, ContactAttempt, Fax, FhbfMeasure } from '../../../common/types/client';
import type { DecryptedItem } from '../../../common/types/items';
import type { StatisticsFilter } from '../../../common/types/statistics/statistics';
import statistics from '../../../common/helpers/statistics';
import filter from '../../../common/helpers/filter';
import {
  InterventionCenterStatistics,
  InterventionCenterOptionStatistic,
  InterventionCenterFaxStatistic,
  InterventionCenterMeasureStatistic,
  InterventionCenterFaxPerPoliceStation,
} from '../../../common/types/statistics/fhbfStatistics';
import { Field } from '../../../common/types/form';
import policeMeasureForm from '../../../clients/detail/fhbfForm/interventionCenter/policeMeasureForm';
import anamnesisForm from '../../../clients/detail/fhbfForm/interventionCenter/anamnesisForm';
import contactForm from '../../../clients/detail/fhbfForm/interventionCenter/contactForm';
import onwardPlacementForm from '../../../clients/detail/fhbfForm/interventionCenter/onwardPlacementForm';
import { Documentation } from '../../../common/types/documentation';
import { didMeasureTakePlace, isClientRelevantForStatistic, isMeasureCounseling } from '../helpers';
import faxForm from '../../../clients/detail/fhbfForm/fax/faxForm';
import formatter from '../../../common/helpers/formatter';

const months = [
  {
    value: 0,
    name: 'Januar',
  },
  {
    value: 1,
    name: 'Februar',
  },
  {
    value: 2,
    name: 'März',
  },
  {
    value: 3,
    name: 'April',
  },
  {
    value: 4,
    name: 'Mai',
  },
  {
    value: 5,
    name: 'Juni',
  },
  {
    value: 6,
    name: 'Juli',
  },
  {
    value: 7,
    name: 'August',
  },
  {
    value: 8,
    name: 'September',
  },
  {
    value: 9,
    name: 'Oktober',
  },
  {
    value: 10,
    name: 'November',
  },
  {
    value: 11,
    name: 'Dezember',
  },
];

const clientOptionsKeyFieldsMapping: {
  key: keyof InterventionCenterOptionStatistic;
  fields: Field<Client>[];
}[] = [
  {
    key: 'policeMeasure',
    fields: policeMeasureForm.fields,
  },
  {
    key: 'typeOfInitialContact',
    fields: contactForm.fields,
  },
  {
    key: 'age',
    fields: anamnesisForm.fields,
  },
  {
    key: 'origin',
    fields: anamnesisForm.fields,
  },
  {
    key: 'personExercisingViolence',
    fields: anamnesisForm.fields,
  },
  {
    key: 'onwardPlacement',
    fields: onwardPlacementForm.fields,
  },
  {
    key: 'nationality',
    fields: anamnesisForm.fields,
  },
];

const getClientOptionStatistic = (clients: DecryptedItem<Client>[]): InterventionCenterOptionStatistic => {
  return clientOptionsKeyFieldsMapping.reduce(
    (optionStatistic, { key, fields }) => ({
      ...optionStatistic,
      [key]: statistics.getStatisticByOptions(clients, key, fields),
    }),
    {
      policeMeasure: {},
      typeOfInitialContact: {},
      age: {},
      origin: {},
      personExercisingViolence: {},
      onwardPlacement: {},
      nationality: {},
    },
  );
};

const getFaxesPerPoliceStation = (faxes: Fax[], policeStation: string): InterventionCenterFaxPerPoliceStation => {
  const filteredFaxes = faxes.filter((fax) => fax.policeStation === policeStation);
  const hgwClerk = filteredFaxes.filter((fax) => fax.sentFrom === 'hgwClerk');
  const patrolman = filteredFaxes.filter((fax) => fax.sentFrom === 'patrolman');
  const bpfk = filteredFaxes.filter((fax) => fax.sentFrom === 'bpfk');
  return {
    policeStation,
    numberOfFaxes: filteredFaxes.length,
    numberOfHgwClerk: hgwClerk.length,
    numberOfPatrolman: patrolman.length,
    numberOfBpfk: bpfk.length,
  };
};

const getFaxesStatistics = (
  clients: DecryptedItem<Client>[],
  statisticsFilter: StatisticsFilter,
): InterventionCenterFaxStatistic => {
  const filteredFaxes: Fax[] = _.flatten(
    clients.map((client) => {
      const faxes = (client.payload.faxes || []).filter((fax) => filter.isInYear(fax.date, statisticsFilter.year));
      return faxes;
    }),
  );

  const years = Array.from(
    new Set(
      filteredFaxes
        .map((value) => (value.date != null ? new Date(value.date).getFullYear() : undefined))
        .filter(<T extends number>(year?: T): year is T => year !== undefined),
    ),
  );

  const numberOfFaxesPerMonth = _.flatten(
    years.map((year: number) => {
      const faxesPerMonth = months.map((month) => {
        const faxes = filteredFaxes.filter((fax) => {
          if (fax.date != null) {
            const faxDate = new Date(fax.date);
            const faxDateYear = faxDate.getFullYear();
            const faxDateMonth = faxDate.getMonth();
            return faxDateYear === year && faxDateMonth === month.value;
          }
          return false;
        });
        return { dateLabel: `${month.name} ${year}`, numberOfFaxes: faxes.length };
      });

      const numberOfFaxes = faxesPerMonth.reduce((prevValue, { numberOfFaxes }) => prevValue + numberOfFaxes, 0);

      return [{ dateLabel: `${year}`, numberOfFaxes }, ...faxesPerMonth];
    }),
  );

  const policeStationOptions = formatter.getFieldSelectOptions('policeStation', faxForm.fields);

  const faxesPerPoliceStations = policeStationOptions.map((option) =>
    getFaxesPerPoliceStation(filteredFaxes, option.value),
  );

  return {
    numberOfFaxes: filteredFaxes.length,
    numberOfFaxesPerMonth,
    faxesPerPoliceStations,
  };
};

const getMeasureStatistics = (
  clients: DecryptedItem<Client>[],
  statisticsFilter: StatisticsFilter,
): InterventionCenterMeasureStatistic => {
  const filteredClients = clients
    .filter((decryptedClient) => {
      const measures = (decryptedClient.payload.measures || []).filter(
        (measure: FhbfMeasure) => didMeasureTakePlace(measure) && isMeasureCounseling(measure),
      );
      return measures.length > 0;
    })
    .map((decryptedClient) => {
      const measures = (decryptedClient.payload.measures || []).filter(
        (measure) => didMeasureTakePlace(measure) && isMeasureCounseling(measure),
      );
      return {
        ...decryptedClient,
        payload: {
          ...decryptedClient.payload,
          measures,
        },
      };
    });

  const filteredMeasures: FhbfMeasure[] = _.flatten(
    clients.map((client) => {
      const measures = (client.payload.measures || []).filter(
        (measure: FhbfMeasure) => filter.isInYear(measure.date, statisticsFilter.year) && didMeasureTakePlace(measure),
      );
      return measures;
    }),
  );

  const calculateValue = (hasIncreased: boolean, currentValue: number): number => {
    return hasIncreased ? currentValue + 1 : currentValue;
  };

  const clientMeasureStatistics = filteredClients.reduce(
    (statistics, client) => {
      const numberOfWomenWithOneMeasure = calculateValue(
        (client.payload.measures || []).length === 1,
        statistics.numberOfWomenWithOneMeasure,
      );
      const numberOfWomenWithTwoMeasures = calculateValue(
        (client.payload.measures || []).length === 2,
        statistics.numberOfWomenWithTwoMeasures,
      );
      const numberOfWomenWithThreeMeasures = calculateValue(
        (client.payload.measures || []).length === 3,
        statistics.numberOfWomenWithThreeMeasures,
      );
      const numberOfWomenWithFourMeasures = calculateValue(
        (client.payload.measures || []).length === 4,
        statistics.numberOfWomenWithFourMeasures,
      );
      const numberOfWomenWithFiveAndMoreMeasures = calculateValue(
        (client.payload.measures || []).length >= 5,
        statistics.numberOfWomenWithFiveAndMoreMeasures,
      );
      return {
        numberOfWomenWithOneMeasure,
        numberOfWomenWithTwoMeasures,
        numberOfWomenWithThreeMeasures,
        numberOfWomenWithFourMeasures,
        numberOfWomenWithFiveAndMoreMeasures,
      };
    },
    {
      numberOfWomenWithOneMeasure: 0,
      numberOfWomenWithTwoMeasures: 0,
      numberOfWomenWithThreeMeasures: 0,
      numberOfWomenWithFourMeasures: 0,
      numberOfWomenWithFiveAndMoreMeasures: 0,
    },
  );

  const measureStatistics = filteredMeasures.reduce(
    (statistic, measure) => {
      const numberOfTranslation = calculateValue(measure.translator === true, statistic.numberOfTranslation);
      const numberOfRiskQuestionnaire = calculateValue(
        measure.riskQuestionnaire === true,
        statistic.numberOfRiskQuestionnaire,
      );
      const numberOfTypeTelephoneConsultation = calculateValue(
        measure.type === 'telephoneConsultation',
        statistic.numberOfTypeTelephoneConsultation,
      );
      const numberOfTypePersonalConsultation = calculateValue(
        measure.type === 'personalConsultation',
        statistic.numberOfTypePersonalConsultation,
      );
      const numberOfTypeAccompaniment = calculateValue(
        measure.type === 'accompaniment',
        statistic.numberOfTypeOnlineConsultation,
      );
      const numberOfTypeOnlineConsultation = calculateValue(
        measure.type === 'onlineConsultation',
        statistic.numberOfTypeOnlineConsultation,
      );
      const numberOfTypeFollowUp = calculateValue(
        measure.type === 'followUp',
        statistic.numberOfTypeOnlineConsultation,
      );
      const numberOfTopicDomesticViolence = calculateValue(
        (measure.topic || []).includes('domesticViolence'),
        statistic.numberOfTopicDomesticViolence,
      );
      const numberOfTopicStalking = calculateValue(
        (measure.topic || []).includes('stalking'),
        statistic.numberOfTopicStalking,
      );
      const numberOfTopicBullying = calculateValue(
        (measure.topic || []).includes('bullying'),
        statistic.numberOfTopicBullying,
      );
      const numberOfTopicFgm = calculateValue((measure.topic || []).includes('fgm'), statistic.numberOfTopicFgm);
      const numberOfTopicPsychosocialCounseling = calculateValue(
        (measure.topic || []).includes('psychosocialCounseling'),
        statistic.numberOfTopicPsychosocialCounseling,
      );
      const numberOfTopicTraumaCounseling = calculateValue(
        (measure.topic || []).includes('traumaCounseling'),
        statistic.numberOfTopicTraumaCounseling,
      );
      const numberOfTopicOther = calculateValue((measure.topic || []).includes('other'), statistic.numberOfTopicOther);
      const durationUpToThirtyMinutes = calculateValue(
        (measure.duration || '') > '0' && (measure.duration || '') <= '30',
        statistic.durationUpToThirtyMinutes,
      );
      const durationBetweenThirtyOneAndSixtyMinutes = calculateValue(
        (measure.duration || '') > '30' && (measure.duration || '') >= '60',
        statistic.durationBetweenThirtyOneAndSixtyMinutes,
      );
      const durationBetweenSixtyOneAndNinetyMinutes = calculateValue(
        (measure.duration || '') > '60' && (measure.duration || '') >= '90',
        statistic.durationBetweenSixtyOneAndNinetyMinutes,
      );
      const durationOverNintyMinutes = calculateValue(
        (measure.duration || '') > '90',
        statistic.durationOverNintyMinutes,
      );
      return {
        numberOfTranslation,
        numberOfRiskQuestionnaire,
        numberOfTypeTelephoneConsultation,
        numberOfTypePersonalConsultation,
        numberOfTypeAccompaniment,
        numberOfTypeOnlineConsultation,
        numberOfTypeFollowUp,
        numberOfTopicDomesticViolence,
        numberOfTopicStalking,
        numberOfTopicBullying,
        numberOfTopicFgm,
        numberOfTopicPsychosocialCounseling,
        numberOfTopicTraumaCounseling,
        numberOfTopicOther,
        durationUpToThirtyMinutes,
        durationBetweenThirtyOneAndSixtyMinutes,
        durationBetweenSixtyOneAndNinetyMinutes,
        durationOverNintyMinutes,
      };
    },
    {
      numberOfTranslation: 0,
      numberOfRiskQuestionnaire: 0,
      numberOfTypeTelephoneConsultation: 0,
      numberOfTypePersonalConsultation: 0,
      numberOfTypeAccompaniment: 0,
      numberOfTypeOnlineConsultation: 0,
      numberOfTypeFollowUp: 0,
      numberOfTopicDomesticViolence: 0,
      numberOfTopicStalking: 0,
      numberOfTopicBullying: 0,
      numberOfTopicFgm: 0,
      numberOfTopicPsychosocialCounseling: 0,
      numberOfTopicTraumaCounseling: 0,
      numberOfTopicOther: 0,
      durationUpToThirtyMinutes: 0,
      durationBetweenThirtyOneAndSixtyMinutes: 0,
      durationBetweenSixtyOneAndNinetyMinutes: 0,
      durationOverNintyMinutes: 0,
    },
  );

  const totalNumberOfMeasures = filteredMeasures.filter(
    (measure) => didMeasureTakePlace(measure) && isMeasureCounseling(measure),
  ).length;

  return {
    totalNumberOfMeasures,
    numberOfWomenAdvised: filteredClients.length,
    ...clientMeasureStatistics,
    ...measureStatistics,
  };
};

const getNumberOfContactAttempts = (clients: DecryptedItem<Client>[], statisticsFilter: StatisticsFilter): number => {
  const contactAttempts: FhbfMeasure[] = _.flatten(
    clients.map((client) =>
      (client.payload.contactAttempts || []).filter(
        (contactAttempt: ContactAttempt) =>
          filter.isInYear(contactAttempt.date, statisticsFilter.year) &&
          contactAttempt.typeOfContact !== 'noContactPossible',
      ),
    ),
  );
  return contactAttempts.length;
};

const getFilteredClients = (
  clients: DecryptedItem<Client>[],
  statisticsFilter: StatisticsFilter,
): DecryptedItem<Client>[] => {
  return clients
    .filter((client) => isClientRelevantForStatistic(client, statisticsFilter.year, statisticsFilter.advisor))
    .map((decryptedClient) => {
      const measures = (decryptedClient.payload.measures || []).filter((measure) =>
        filter.isInYear(measure.date, statisticsFilter.year),
      );
      return {
        ...decryptedClient,
        payload: {
          ...decryptedClient.payload,
          measures,
        },
      };
    });
};

const getShortContacts = (documentation: Documentation[], statisticsFilter: StatisticsFilter): number => {
  const shortContacts = documentation.filter((docu) => {
    const isInDateRange = filter.isInYear(docu.date, statisticsFilter.year);
    const isShortContacts = docu.type === 'shortContact';

    return isInDateRange && isShortContacts;
  });

  return shortContacts.reduce((sum, docu) => sum + (docu.numberOfShortContact || 0), 0);
};

const createInterventionCenterStatistics = (
  clients: DecryptedItem<Client>[],
  documentation: Documentation[],
  statisticsFilter: StatisticsFilter,
): InterventionCenterStatistics => {
  const filteredClients = getFilteredClients(clients, statisticsFilter);
  const numberOfShortContacts = getShortContacts(documentation, statisticsFilter);

  const optionClientStatistic = getClientOptionStatistic(filteredClients);
  const faxesStatistic = getFaxesStatistics(filteredClients, statisticsFilter);
  const measureStatistics = getMeasureStatistics(filteredClients, statisticsFilter);
  const numberOfContactAttempts = getNumberOfContactAttempts(clients, statisticsFilter);

  const numberOfSelfReporter = statistics.getNumberOfValues(filteredClients, 'selfReporter');
  const numberOfWaywards = statistics.getNumberOfValues(filteredClients, 'wayward');
  const numberOfRepeatCases = statistics.getNumberOfValues(filteredClients, 'repeatCase');
  const numberOfChildren = statistics.getNumberOfValues(filteredClients, 'children');
  const numberOfChildrenPresent = statistics.getSumOfValues(filteredClients, 'numberOfChildrenPresent');
  const numberOfChildrenZeroToThreeYears = statistics.getSumOfValues(filteredClients, 'ageOfChildrenZeroToThreeYears');
  const numberOfChildrenFourToSixYears = statistics.getSumOfValues(filteredClients, 'ageOfChildrenFourToSixYears');
  const numberOfChildrenSevenToThirteenYears = statistics.getSumOfValues(
    filteredClients,
    'ageOfChildrenSevenToThirteenYears',
  );
  const numberOfChildrenFourteenToSeventeenYears = statistics.getSumOfValues(
    filteredClients,
    'ageOfChildrenFourteenToSeventeenYears',
  );
  const numberOfPregnancy = statistics.getNumberOfValues(filteredClients, 'pregnancy');
  const numberOfDomesticViolence = statistics.getNumberOfValues(filteredClients, 'domesticViolence');
  const numberOfExPartnerStalking = statistics.getNumberOfValues(filteredClients, 'exPartnerStalking');
  const numberOfStabilizationGroup = statistics.getNumberOfValues(filteredClients, 'stabilizationGroup');
  const numberOfTraumaCounseling = statistics.getNumberOfValues(filteredClients, 'traumaCounseling');
  const numberOfNoConsultationRequest = statistics.getNumberOfValues(filteredClients, 'noConsultationRequest');

  const clientsWithOnwardPlacementDirectContact = filteredClients.filter(
    ({ payload }) =>
      payload.onwardPlacementDirectContact === true &&
      (payload.onwardPlacementContactDetails === undefined || payload.onwardPlacementContactDetails === false) &&
      (payload.onwardPlacement === undefined || payload.onwardPlacement.length === 0),
  );
  const clientsWithOnwardPlacementContactDetails = filteredClients.filter(
    ({ payload }) =>
      (payload.onwardPlacementDirectContact === undefined || payload.onwardPlacementDirectContact === false) &&
      (payload.onwardPlacementContactDetails === true ||
        (payload.onwardPlacement !== undefined && payload.onwardPlacement.length > 0)),
  );
  const clientsWithBothOnwardPlacement = filteredClients.filter(
    ({ payload }) =>
      payload.onwardPlacementDirectContact === true &&
      (payload.onwardPlacementContactDetails === true ||
        (payload.onwardPlacement !== undefined && payload.onwardPlacement.length > 0)),
  );

  return {
    type: 'interventionCenter',
    totalNumber: filteredClients.length,
    numberOfSelfReporter,
    numberOfWaywards,
    numberOfRepeatCases,
    numberOfChildren,
    numberOfChildrenPresent,
    numberOfChildrenZeroToThreeYears,
    numberOfChildrenFourToSixYears,
    numberOfChildrenSevenToThirteenYears,
    numberOfChildrenFourteenToSeventeenYears,
    numberOfPregnancy,
    numberOfExPartnerStalking,
    numberOfDomesticViolence,
    numberOfStabilizationGroup,
    numberOfTraumaCounseling,
    numberOfNoConsultationRequest,
    numberOfContactAttempts: numberOfContactAttempts + numberOfShortContacts,
    numberOfOnwardPlacementDirectContact: clientsWithOnwardPlacementDirectContact.length,
    numberOfOnwardPlacementContactDetails: clientsWithOnwardPlacementContactDetails.length,
    numberOfBothOnwardPlacement: clientsWithBothOnwardPlacement.length,
    ...faxesStatistic,
    ...measureStatistics,
    ...optionClientStatistic,
  };
};

export default createInterventionCenterStatistics;
